import {
  KEY_ACTIONS, PAD_ACTIONS, RESERVED_KEYS, SLOTS, codeName, keyBindings, keyBindingsAreDefault, onBindingsChange,
  padBindingName, padBindings, padBindingsAreDefault, resetKeyBindings, resetPadBindings, setKeyBinding, setPadBinding,
  type Action, type PadAction,
} from '../game/bindings.ts'
import { capturePadButton, setPadMenuBack } from '../game/gamepad.ts'
import { preferences, setPreference, type TouchChoice } from '../game/preferences.ts'

/**
 * The Settings screen: every action with two slots for keys or buttons, a tab each for the
 * keyboard and mouse and for a controller, and a reset back to Halo's layout; and a tab of
 * options (preferences.ts).
 *
 * Choose a slot, then press what it should be. Mouse buttons and the wheel bind like keys, so
 * Escape is the way out of a slot, and Delete or Backspace empties it. On a controller, holding
 * the button for a moment binds the hold, which is how one button both reloads and picks up.
 */

type Tab = 'keys' | 'pad' | 'options'

const CSS = `
.ctl-scrim{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:16px;box-sizing:border-box;
  background:rgba(2,6,12,.72);color:#bfe4ff;font:14px/1.45 ui-sans-serif,system-ui,sans-serif;-webkit-user-select:none;user-select:none;touch-action:auto}
.ctl-scrim[hidden]{display:none}
.ctl-panel{width:min(640px,100%);max-height:calc(100dvh - 32px);display:flex;flex-direction:column;box-sizing:border-box;
  background:rgba(6,18,28,.96);border:1px solid #3f8f7d;border-radius:8px;box-shadow:0 18px 60px rgba(0,0,0,.55)}
.ctl-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px 0}
.ctl-head h2{margin:0;font-size:15px;font-weight:600;letter-spacing:.24em;text-transform:uppercase;color:#d8f2ff}
.ctl-close{width:36px;height:36px;border:0;border-radius:5px;background:none;color:#bfe4ff;font:inherit;font-size:22px;line-height:1;cursor:pointer}
.ctl-close:hover{background:rgba(191,228,255,.08)}
.ctl-tabs{display:flex;gap:4px;padding:12px 16px 0;border-bottom:1px solid rgba(63,143,125,.45)}
.ctl-tab{flex:1;padding:10px 8px;border:1px solid transparent;border-bottom:0;border-radius:5px 5px 0 0;background:none;color:#9fc4d8;
  font:inherit;font-weight:600;font-size:13px;letter-spacing:.06em;cursor:pointer;margin-bottom:-1px}
.ctl-tab[aria-selected=true]{background:rgba(12,34,50,.95);border-color:rgba(63,143,125,.45);color:#d8f2ff}
.ctl-list{overflow-y:auto;overscroll-behavior:contain;padding:4px 16px 8px;flex:1;min-height:0}
.ctl-group{margin:14px 0 4px;font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#7fb8a8}
.ctl-row{display:grid;grid-template-columns:minmax(0,1fr) repeat(${SLOTS},minmax(84px,128px));gap:6px;align-items:center;padding:4px 0;
  border-top:1px solid rgba(191,228,255,.06)}
.ctl-label{min-width:0;color:#d8f2ff}
.ctl-label small{display:block;font-size:11px;color:#86a6b8;line-height:1.3}
.ctl-slot{min-height:36px;padding:6px 8px;border:1px solid rgba(63,143,125,.7);border-radius:5px;background:rgba(10,28,40,.9);color:#e4f6ff;
  font:inherit;font-size:13px;font-weight:600;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ctl-slot:hover{border-color:#7fd8ff}
.ctl-slot.empty{color:#5d7a8a;font-weight:400}
.ctl-slot.listening{border-color:#f5d76c;color:#f5d76c;background:rgba(245,215,108,.08);animation:ctl-pulse 1s ease-in-out infinite}
@keyframes ctl-pulse{50%{border-color:rgba(245,215,108,.35)}}
.ctl-status{margin:0;padding:8px 16px;min-height:20px;font-size:12px;color:#9fc4d8;border-top:1px solid rgba(63,143,125,.3)}
.ctl-status.notice{color:#f5d76c}
.ctl-status:empty{visibility:hidden}
.ctl-foot{display:flex;justify-content:space-between;gap:8px;padding:0 16px 14px}
.ctl-foot button{min-height:40px;padding:8px 16px;border-radius:5px;font:inherit;font-weight:600;font-size:13px;cursor:pointer}
.ctl-reset{border:1px solid rgba(255,143,122,.5);background:rgba(255,90,60,.08);color:#ffd4cb}
.ctl-reset.confirm{background:#c8442c;border-color:#c8442c;color:#fff}
.ctl-reset:disabled{opacity:.4;cursor:default}
.ctl-done{border:0;background:#bfeee0;color:#102824;margin-left:auto}
.ctl-scrim :focus-visible{outline:2px solid #8fd3ff;outline-offset:2px}
.ctl-choices{display:flex;gap:6px;flex-shrink:0}
.ctl-choice{min-width:56px;min-height:36px;padding:6px 12px;border:1px solid rgba(63,143,125,.7);border-radius:5px;background:rgba(10,28,40,.9);color:#9fc4d8;
  font:inherit;font-size:13px;font-weight:600;cursor:pointer}
.ctl-choice:hover{border-color:#7fd8ff}
.ctl-choice[aria-pressed=true]{background:#bfeee0;border-color:#bfeee0;color:#102824}
.ctl-option{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:10px 0;border-top:1px solid rgba(191,228,255,.06)}
@media (max-width:480px){.ctl-row{grid-template-columns:minmax(0,1fr) repeat(${SLOTS},minmax(70px,92px))}.ctl-label small{display:none}.ctl-slot{font-size:12px;padding:6px 4px}}
@media (max-height:480px){.ctl-head{padding-top:8px}.ctl-tabs{padding-top:6px}.ctl-tab{padding:6px}.ctl-foot{padding-bottom:8px}.ctl-foot button{min-height:34px}}
@media (prefers-reduced-motion:reduce){.ctl-slot.listening{animation:none}}
`

let scrim: HTMLElement | null = null
let tab: Tab = 'keys'
let opener: HTMLElement | null = null
/** The slot waiting for a key or button. */
let listening: { action: string; slot: number; cancelPad?: () => void } | null = null
let swallowClick = false
let resetTimer: ReturnType<typeof setTimeout> | undefined

const label = (action: string) => [...KEY_ACTIONS, ...PAD_ACTIONS].find(a => a.id === action)?.label ?? action

/** Open the Controls screen on a tab; focus goes back to `from` when it closes. */
export function openControls(initial: Tab, from?: HTMLElement): void {
  opener = from ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
  if (document.pointerLockElement) document.exitPointerLock()
  const root = build()
  root.hidden = false
  setTab(initial)
  setPadMenuBack(close)
  addEventListener('keydown', onKey, true)
  addEventListener('mousedown', onMouse, true)
  addEventListener('click', onClick, true)
  addEventListener('contextmenu', onContextMenu, true)
  addEventListener('wheel', onWheel, { capture: true, passive: false })
  root.querySelector<HTMLElement>(`.ctl-tab[data-tab="${tab}"]`)?.focus({ preventScroll: true })
}

function close(): void {
  if (!scrim || scrim.hidden) return
  stopListening()
  scrim.hidden = true
  setPadMenuBack(null)
  removeEventListener('keydown', onKey, true)
  removeEventListener('mousedown', onMouse, true)
  removeEventListener('click', onClick, true)
  removeEventListener('contextmenu', onContextMenu, true)
  removeEventListener('wheel', onWheel, true)
  opener?.focus({ preventScroll: true })
}

function build(): HTMLElement {
  if (scrim) return scrim
  const style = document.createElement('style')
  style.textContent = CSS
  document.head.append(style)
  scrim = document.createElement('div')
  scrim.className = 'ctl-scrim'
  scrim.hidden = true
  scrim.setAttribute('role', 'dialog')
  scrim.setAttribute('aria-modal', 'true')
  scrim.setAttribute('aria-labelledby', 'ctl-title')
  scrim.innerHTML = `<div class="ctl-panel">
    <div class="ctl-head"><h2 id="ctl-title">Settings</h2><button class="ctl-close" type="button" aria-label="Close settings">×</button></div>
    <div class="ctl-tabs" role="tablist">
      <button class="ctl-tab" role="tab" type="button" data-tab="keys" aria-controls="ctl-list">Keyboard &amp; mouse</button>
      <button class="ctl-tab" role="tab" type="button" data-tab="pad" aria-controls="ctl-list">Controller</button>
      <button class="ctl-tab" role="tab" type="button" data-tab="options" aria-controls="ctl-list">Options</button>
    </div>
    <div class="ctl-list" id="ctl-list" role="tabpanel" data-pad-scroll></div>
    <p class="ctl-status" aria-live="polite"></p>
    <div class="ctl-foot"><button class="ctl-reset" type="button"></button><button class="ctl-done" type="button">Done</button></div>
  </div>`
  scrim.querySelector('.ctl-close')!.addEventListener('click', close)
  scrim.querySelector('.ctl-done')!.addEventListener('click', close)
  scrim.querySelector('.ctl-reset')!.addEventListener('click', onReset)
  for (const button of scrim.querySelectorAll<HTMLElement>('.ctl-tab')) button.addEventListener('click', () => setTab(button.dataset.tab as Tab))
  scrim.querySelector('.ctl-list')!.addEventListener('click', event => {
    const slot = (event.target as HTMLElement).closest<HTMLElement>('.ctl-slot')
    if (slot) listen(slot.dataset.action!, Number(slot.dataset.slot))
    const choice = (event.target as HTMLElement).closest<HTMLElement>('.ctl-choice')
    if (choice) choose(choice.dataset.option!, choice.dataset.value!)
  })
  // A click on the dim backdrop, outside the panel, closes it as Done would.
  scrim.addEventListener('mousedown', event => { if (event.target === scrim && !listening) close() })
  onBindingsChange(() => { if (scrim && !scrim.hidden) render() })
  document.body.append(scrim)
  return scrim
}

function setTab(next: Tab): void {
  stopListening()
  tab = next
  for (const button of scrim!.querySelectorAll<HTMLElement>('.ctl-tab')) button.setAttribute('aria-selected', String(button.dataset.tab === tab))
  status(tab === 'keys' ? 'Choose a slot, then press a key or mouse button.'
    : tab === 'pad' ? 'Choose a slot, then press a controller button. Keep it held to bind a hold.'
    : '')
  render()
  scrim!.querySelector('.ctl-list')!.scrollTop = 0
}

function render(): void {
  const list = scrim!.querySelector<HTMLElement>('.ctl-list')!
  const reset = scrim!.querySelector<HTMLButtonElement>('.ctl-reset')!
  reset.hidden = tab === 'options'
  if (tab === 'options') { renderOptions(list); return }
  const focused = document.activeElement instanceof HTMLElement && list.contains(document.activeElement)
    ? `${document.activeElement.dataset.action}:${document.activeElement.dataset.slot}` : null
  const actions = tab === 'keys' ? KEY_ACTIONS : PAD_ACTIONS
  let html = '', group = ''
  for (const { id, label, note, group: heading } of actions) {
    if (heading !== group) { group = heading; html += `<div class="ctl-group">${heading}</div>` }
    const names = tab === 'keys' ? keyBindings(id as Action).map(codeName) : padBindings(id as PadAction).map(padBindingName)
    html += `<div class="ctl-row"><div class="ctl-label">${label}${note ? `<small>${note}</small>` : ''}</div>`
    for (let slot = 0; slot < SLOTS; slot++) {
      const isListening = listening?.action === id && listening.slot === slot
      const name = names[slot]
      const text = isListening ? (tab === 'keys' ? 'Press a key…' : 'Press a button…') : name ?? '—'
      const spoken = `${label}, ${slot === 0 ? 'primary' : 'alternate'}: ${name ?? 'unbound'}`
      html += `<button class="ctl-slot${name || isListening ? '' : ' empty'}${isListening ? ' listening' : ''}" type="button" data-action="${id}" data-slot="${slot}" aria-label="${escapeAttr(spoken)}">${escapeHtml(text)}</button>`
    }
    html += '</div>'
  }
  list.innerHTML = html
  if (focused) list.querySelector<HTMLElement>(`[data-action="${focused.split(':')[0]}"][data-slot="${focused.split(':')[1]}"]`)?.focus({ preventScroll: true })
  clearTimeout(resetTimer)
  reset.classList.remove('confirm')
  reset.textContent = tab === 'keys' ? 'Reset keyboard to defaults' : 'Reset controller to defaults'
  reset.disabled = tab === 'keys' ? keyBindingsAreDefault() : padBindingsAreDefault()
}

const OPTIONS: readonly { id: 'visor' | 'touch'; label: string; note: string; values: readonly [string, string][] }[] = [
  { id: 'visor', label: 'Visor outline', note: 'The faint edge of the helmet\'s visor around the view in first person.', values: [['on', 'On'], ['off', 'Off']] },
  { id: 'touch', label: 'Touch controls', note: 'On-screen buttons, or keyboard and mouse on a touch screen. Takes effect when a match starts.',
    values: [['auto', 'Auto'], ['on', 'On'], ['off', 'Off']] },
]

function optionValue(id: 'visor' | 'touch'): string {
  const prefs = preferences()
  return id === 'visor' ? (prefs.visor ? 'on' : 'off') : prefs.touch
}

function renderOptions(list: HTMLElement): void {
  const focused = document.activeElement instanceof HTMLElement && list.contains(document.activeElement) ? document.activeElement.dataset.option : undefined
  list.innerHTML = OPTIONS.map(({ id, label, note, values }) => `<div class="ctl-option"><div class="ctl-label" id="ctl-opt-${id}">${label}<small>${note}</small></div>
    <div class="ctl-choices" role="group" aria-labelledby="ctl-opt-${id}">${values.map(([value, text]) =>
      `<button class="ctl-choice" type="button" data-option="${id}" data-value="${value}" aria-pressed="${optionValue(id) === value}">${text}</button>`).join('')}</div></div>`).join('')
  if (focused) list.querySelector<HTMLElement>(`.ctl-choice[data-option="${focused}"][aria-pressed=true]`)?.focus({ preventScroll: true })
}

function choose(option: string, value: string): void {
  if (option === 'visor') setPreference('visor', value === 'on')
  else if (option === 'touch') setPreference('touch', value as TouchChoice)
  render()
}

function listen(action: string, slot: number): void {
  stopListening()
  listening = { action, slot }
  if (tab === 'pad') {
    const held = PAD_ACTIONS.find(a => a.id === action)?.held
    listening.cancelPad = capturePadButton(!held, binding => {
      listening = null
      if (binding) assign(action, slot, padBindingName(binding), () => setPadBinding(action as PadAction, slot, binding))
      else { status('Cancelled.'); render() }
    })
    status(`${label(action)}: press a controller button${held ? '' : ', or hold one to bind the hold'}. Start or Escape cancels; Delete clears.`, true)
  } else status(`${label(action)}: press a key or mouse button. Escape cancels; Delete clears.`, true)
  render()
}

function stopListening(): void {
  listening?.cancelPad?.()
  listening = null
}

/** Make a binding and say what it was taken from, if anything. */
function assign(action: string, slot: number, name: string, apply: () => string | undefined): void {
  listening?.cancelPad?.()
  listening = null
  const displaced = apply()
  render()
  status(displaced ? `${name} moved here from ${label(displaced)}.` : `${label(action)}: ${name}.`, !!displaced)
  scrim!.querySelector<HTMLElement>(`.ctl-slot[data-action="${action}"][data-slot="${Math.min(slot, (tab === 'keys' ? keyBindings(action as Action) : padBindings(action as PadAction)).length)}"]`)
    ?.focus({ preventScroll: false })
}

function bindKey(code: string): void {
  const { action, slot } = listening!
  if (RESERVED_KEYS.has(code)) return
  assign(action, slot, codeName(code), () => setKeyBinding(action as Action, slot, code))
}

function clearSlot(): void {
  const { action, slot } = listening!
  assign(action, slot, '', () => tab === 'keys' ? setKeyBinding(action as Action, slot, null) : setPadBinding(action as PadAction, slot, null))
  status(`${label(action)}: slot cleared.`)
}

function onKey(event: KeyboardEvent): void {
  if (!scrim || scrim.hidden) return
  if (listening) {
    event.preventDefault()
    event.stopImmediatePropagation()
    if (event.repeat) return
    if (event.code === 'Escape') { stopListening(); render(); status('Cancelled.'); return }
    if (event.code === 'Delete' || event.code === 'Backspace') { clearSlot(); return }
    if (tab === 'keys') bindKey(event.code)
    return
  }
  // Not binding: the screen is a modal, so the game's keys (scoreboard, mute) do not reach it.
  if (event.code === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close(); return }
  if (event.code !== 'Tab' && event.code !== 'Enter' && event.code !== 'Space' && !event.code.startsWith('Arrow')) event.stopImmediatePropagation()
}

function onMouse(event: MouseEvent): void {
  // A press that bound a button but never became a click (released off the window) must not eat the next real click.
  swallowClick = false
  if (!listening) return
  if (tab === 'pad') { stopListening(); render(); return }
  event.preventDefault()
  event.stopImmediatePropagation()
  // The click that follows this press must not also choose whatever slot is under the pointer.
  swallowClick = true
  bindKey(`Mouse${event.button}`)
}

function onClick(event: MouseEvent): void {
  if (!swallowClick) return
  swallowClick = false
  event.preventDefault()
  event.stopImmediatePropagation()
}

function onContextMenu(event: MouseEvent): void {
  if (scrim && !scrim.hidden) event.preventDefault()
}

function onWheel(event: WheelEvent): void {
  if (!listening || tab !== 'keys' || event.deltaY === 0) return
  event.preventDefault()
  event.stopImmediatePropagation()
  bindKey(event.deltaY < 0 ? 'WheelUp' : 'WheelDown')
}

function onReset(): void {
  const reset = scrim!.querySelector<HTMLButtonElement>('.ctl-reset')!
  // Two presses, so one stray click does not throw away a layout someone built up.
  if (!reset.classList.contains('confirm')) {
    reset.classList.add('confirm')
    reset.textContent = 'Press again to reset'
    clearTimeout(resetTimer)
    resetTimer = setTimeout(() => render(), 3000)
    return
  }
  stopListening()
  if (tab === 'keys') resetKeyBindings()
  else resetPadBindings()
  render()
  status(tab === 'keys' ? 'Keyboard and mouse are back to the default layout.' : 'Controller is back to the default layout.', true)
}

function status(text: string, notice = false): void {
  const line = scrim?.querySelector<HTMLElement>('.ctl-status')
  if (!line) return
  line.textContent = text
  line.classList.toggle('notice', notice)
}

const escapeHtml = (text: string) => text.replace(/[&<>]/g, c => `&#${c.charCodeAt(0)};`)
const escapeAttr = (text: string) => text.replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`)
